import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import { ValuationFlow } from '@/types';

export interface VirtualPageViewProps {
  subType: string;
  locale: string;
  stepInputs?: string;
  moreParams?: Record<string, string | number | Array<string>>;
}

export interface FormInputsForTracking {
  propertyType?: PropertyTypeNumber;
  offerType?: OfferType;
  aptDetails?: boolean;
  livingSpace?: number;
  lotSize?: number | null;
  rooms?: number;
  buildYear?: number;
  isRenovated?: boolean;
  renovationYear?: number | null;
  isPropertyOwner?: boolean;
  whenToSell?: number | null;
}

export interface ButtonEventProps {
  event: string;
  category: string;
  action: string;
  label?: string;
}

export const stepInputKeyMapContactModal = {
  firstName: 'input_first_name',
  lastName: 'input_last_name',
  phoneNumber: 'input_phone',
  preferredDay: 'input_date',
  preferredTime: 'input_time',
};

export interface StepInfo {
  name: string;
  step: number;
}

export const stepInputKeyMap: Record<string, StepInfo> = {
  propertyType: {
    name: 'category',
    step: ValuationFlow.PropertyType,
  },
  offerType: {
    name: 'type',
    step: ValuationFlow.OfferType,
  },
  aptDetails: {
    name: 'aptDetails',
    step: ValuationFlow.PropertyDetails - 1,
  },
  livingSpace: {
    name: 'space',
    step: ValuationFlow.PropertyDetails,
  },
  lotSize: {
    name: 'lotSize',
    step: ValuationFlow.PropertyDetails,
  },
  rooms: {
    name: 'room',
    step: ValuationFlow.PropertyDetails,
  },
  buildYear: {
    name: 'yearBuilt',
    step: ValuationFlow.PropertyDetails,
  },
  isRenovated: {
    name: 'renovation',
    step: ValuationFlow.Renovation,
  },
  renovationYear: {
    name: 'renovationYear',
    step: ValuationFlow.RenovationYear,
  },
  isPropertyOwner: {
    name: 'isPropertyOwner',
    step: ValuationFlow.PropertyOwnership,
  },
  whenToSell: {
    name: 'whenToSell',
    step: ValuationFlow.WhenToSell,
  },
};

export enum LANDING_PAGE_BUTTON_EVENT {
  APARTMENT = 'apartment',
  HOUSE = 'house',
  CLICK = 'landingPagePropertyValuation',
}

export enum LANDING_PAGE_BUTTON_ACTION {
  SELECT_PROPERTY_TYPE = 'selectPropertyType',
  OPEN_MARKET_ATLAS = 'open_market_atlas',
}

export enum LOCAL_LANDING_PAGE_EVENT {
  BUTTON_CLICK = 'button_click',
  LINK_CLICK = 'generic_link_click',
  MARKET_MAP_CLICK = 'market_map_interaction',
  CTA_CLICK = 'cta_click',
  FEATURE_DISPLAY = 'feature_displayed',
  FORM_STEPS = 'form_steps',
}

export enum LOCAL_LANDING_PAGE_CATEGORY {
  BUTTON_CLICK = 'button_click',
  LINK_CLICK = 'generic_link_click',
  MARKET_MAP_CLICK = 'market_map_interaction',
  CTA_CLICK = 'cta_click',
  FEATURE_DISPLAY = 'feature_displayed',
  FORM_STEPS = 'form_steps',
}

export enum LOCAL_LANDING_PAGE_ACTION {
  REQUEST_CALLBACK = 'request_callback',
  VALUATION_FUNNEL = 'open_valuation_funnel',
  LLP_CONTENT = 'select_propertyvaluation_content',
  MARKET_MAP_SEARCH = 'market_map_search',
  PRICE_TREND = 'price_trend',
  OPEN_TIPS = 'open_tips',
  MARKET_ATLAS = 'open_market_atlas',
  DISPLAY_CALLBACK = 'display_callback_form',
  INPUT_CALLBACK = 'input_callback_form_info',
  SUBMIT_CALLBACK = 'submit_callback_form',
  INSERT_AD = 'insertAd',
  OPEN_TRANSFER_TAX_INFO = 'open_transfer_tax_info',
  OPEN_MARKET_VALUE_INFO = 'open_market_value_info',
  OPEN_CAPITAL_GAINS_TAX_INFO = 'open_capital_gains_tax_info',
  OPEN_LLP = 'open_local_landing_page',
}

export enum VALUATION_FORM_EVENT {
  FORM_STEPS = 'form_steps',
  FORM_SUBMIT = 'submit_sellerlead',
  FEATURE_DISPLAY = 'feature_displayed',
}

export enum VALUATION_FORM_CATEGORY {
  FORM_STEPS = 'form_steps',
  FORM_SUBMIT = 'submit_sellerlead',
  FEATURE_DISPLAY = 'feature_displayed',
}

export enum TRACKING_CATEGORY {
  BUTTON_CLICK = 'button_click',
  AUTHENTICATION = 'authentication',
  CTA_CLICK = 'cta_click',
  FEATURE_DISPLAY = 'feature_displayed',
}

export enum TRACKING_LABEL {
  VALUATION_FUNNEL = 'valuation_funnel',
  VALUATION_FUNNEL_MIDDLE_BUTTON = 'valuation_funnel_middle_button',
  VALUATION_FUNNEL_TOP_BUTTON = 'valuation_funnel_top_button',
  EMPTY_PROPERTY_OVERVIEW = 'empty_property_overview',
  PROPERTY_OVERVIEW = 'property_overview',
  YEAR_BUILT = 'year_built',
  SHOW_ALL_SELLING = 'own_objects',
  SHOW_ALL_BUYING = 'interested_in_buying',
  SHOW_ALL_OTHER = 'other',
  BADGE_FILTER_BAR = 'filter_bar',
  APARTMENT_SELECTION_MODAL = 'apartment_selection_modal',
  VALUATION_FUNNEL_AFTER_ADDRESS_ENTRY = 'valuation_funnel_after_address_entry',
  MARKET_SITUATION = 'market_situation',
  PROPERTY_VALUATION_LANDING_PAGE = 'propertyvaluation_landingpage',
  VALUATION_EXPIRED_ERR = 'valuation_expired_err',
  VALUATION_FUNNEL_LINK = 'valuation_funnel_link',
  PROPERTY_VALUATION_FORM = 'property_valuation_form',
  FLOATING_BANNER = 'floating_banner',
  PROPERTY_LIMIT_REACHED = 'property_limit_reached',
  VALUATION_HERO_BANNER = 'valuation_hero_banner',
  PROPERTY_OVERVIEW_TEASER = 'property_overview_teaser',
  PROPERTY_PRICE_TREND_TEASER = 'property_price_trend_teaser',
  PROPERTY_SIMILAR_OFFERS_TEASER = 'property_similar_offers_teaser',
  PROPERTY_RENTAL_PRICE_TEASER = 'property_rental_price_teaser',
  HOW_IT_WORKS = 'how_it_works',
  NEXT_STEPS_TEASER = 'next_steps_teaser',
  PROPERTY_VALUATION_MARKET_INSIGHTS = 'property_valuation_market_insights',
  SELLER_LEAD_CARD_CTA = 'seller_lead_card_cta',
  DASHBOARD_CTA = 'dashboard_cta',
}

export enum TRACKING_ACTION {
  VIEW_PROPERTY_DASHBOARD = 'view_property_dashboard',
  SELECT_PROPERTY_TYPE = 'selectPropertyType',
  GO_TO_VALUATION_FORM = 'go_to_valuation_form',
  GO_TO_VALUATION_FUNNEL = 'go_to_propertyvaluation',
  OPEN_BUSINESS_PACKAGE = 'open_business_package',
  PRICE_TREND = 'price_trend',
  OPEN_TOOLTIP_OLD_BUILDING = 'open_tooltip_old_building',
  SHOW_ALL_PROPERTIES = 'show_all_properties',
  BADGE_FILTER_ALL = 'group_by_all',
  BADGE_FILTER_SELLING = 'group_by_own_objects',
  BADGE_FILTER_BUYING = 'group_by_interested_in_buying',
  BADGE_FILTER_OTHER = 'group_by_other',
  CLICK_CANCEL_BUTTON = 'cancel',
  CLICK_CONTINUE_BUTTON = 'continue',
  APARTMENT_SELECTION_MODAL = 'apartment_selection_modal',
  DISPLAY_HOMEOWNER_BANNER = 'display_homeowner_banner',
  DISPLAY_MARKET_SITUATION = 'display_market_situation',
  GO_TO_SUMMARY = 'go_to_summary',
  GO_TO_BREAKDOWN = 'go_to_breakdown',
  SELECT_FOR_SALE = 'select_for_sale',
  SELECT_FOR_RENT = 'select_for_rent',
  OPEN_RENT_LISTING = 'open_rent_listing',
  OPEN_SALE_LISTING = 'open_sale_listing',
  START_SALE = 'start_sale',
  START_RENT = 'start_renting_out',
  GO_TO_MY_REAL_ESTATE = 'go_to_my_real_estate',
  SAVE_PROPERTY_VALUATION_FORM = 'save_property_valuation_form',
  DISPLAY_IAZI_ERROR = 'display_iazi_error',
  DISPLAY_ERROR_MESSAGE = 'display_error_message',
  EDIT_PROPERTY = 'edit_property',
  DELETE_PROPERTY_TRY = 'delete_property_try',
  DELETE_PROPERTY = 'delete_property',
  OPEN_SELLER_LEAD_MODAL = 'open_sellerlead_modal',
  OPEN_INSERT_AD = 'open_insertAd',
  DISPLAY_HOMEOWNER_TEASER = 'display_homeowner_teaser',
  LOG_IN_TO_VIEW_MARKET_VALUATION = 'go_to_login',
  REQUEST_CALLBACK = 'request_callback',
  EXIT_HOME_OWNER_WORLD = 'exit_home_owner_world',
}

export const TRACKING_SHOW_ALL_LABEL = {
  [OfferType.Selling]: TRACKING_LABEL.SHOW_ALL_SELLING,
  [OfferType.Buying]: TRACKING_LABEL.SHOW_ALL_BUYING,
  [OfferType.Other]: TRACKING_LABEL.SHOW_ALL_OTHER,
};

export const TRACKING_BADGE_FILTER_ACTION = {
  all: TRACKING_ACTION.BADGE_FILTER_ALL,
  [OfferType.Selling]: TRACKING_ACTION.BADGE_FILTER_SELLING,
  [OfferType.Buying]: TRACKING_ACTION.BADGE_FILTER_BUYING,
  [OfferType.Other]: TRACKING_ACTION.BADGE_FILTER_OTHER,
};
